/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, observable, reaction} from 'mobx';
import {inject, observer} from 'mobx-react';
import {withRouter, locationShape} from 'react-router';
import autobind from 'autobind-decorator';

import Select from 'spa/Components/Form/Select';

import {cn} from 'spa/css';
import {withI18n} from './Context';

import TypeaheadField from './TypeaheadField';

import './address-field.less';

const ADDRESS_PARTS_SEP = ', ';

@withRouter
@inject(({
  store: {
    session, selection, tracker, sdk, userAddresses,
  },
}, geolocation) => ({
  session,
  selection,
  tracker,
  sdk,
  userAddresses,
  geolocation,
}))
@observer
@withI18n
class AddressField extends React.Component {
  static propTypes = {
    align: PropTypes.oneOf(['left', 'right']),
    hideClearOnEmpty: PropTypes.bool,
    router: PropTypes.object,
    params: PropTypes.object,
    routes: PropTypes.array,
    session: PropTypes.object.isRequired,
    selection: PropTypes.shape({
      address_id: PropTypes.string,
      place_id: PropTypes.string,
    }).isRequired,
    tracker: PropTypes.object.isRequired,
    sdk: PropTypes.object.isRequired,
    userAddresses: PropTypes.object.isRequired,
    geolocation: PropTypes.object.isRequired,
    listClassName: PropTypes.string,
    location: locationShape.isRequired,
    className: PropTypes.string,
    onSelect: PropTypes.func,
    onClear: PropTypes.func,
    defaultValue: PropTypes.string,
    skipInitialLoad: PropTypes.bool,
    skipGeolocation: PropTypes.bool,
    preferGeolocation: PropTypes.bool,
    trackingCategory: PropTypes.string,
    includeClear: PropTypes.bool,
    clearStyles: PropTypes.object,
    darkBackground: PropTypes.bool,
    roundBorder: PropTypes.bool,
    disableSavedAddresses: PropTypes.bool,
    selectedAddressId: PropTypes.string,
    addressConfirmed: PropTypes.bool,
    canLoadCustom: PropTypes.bool,
    placeholder: PropTypes.string,
    selectZIndex: PropTypes.number,
  };

  static defaultProps = {
    align: 'right',
    hideClearOnEmpty: false,
    listClassName: undefined,
  };

  UNSAFE_componentWillMount() {
    const {
      defaultValue, disableSavedAddresses, userAddresses, session, selection,
    } = this.props;

    if (session.user && !userAddresses.isLoading && !userAddresses.isLoaded && !defaultValue && !disableSavedAddresses) {
      userAddresses.maybeLoad();
    }

    //  In case we load the page with clean selection state in the querystring
    if ((selection && !selection.place_id) || !this.props.location.search) {
      this.isCleanLoad = true;
    }
  }

  async componentDidMount() {
    if (
      !this.props.skipInitialLoad &&
      !this.props.defaultValue &&
      !this.props.selection.address_id
    ) {
      const methodsToTry = [this.loadLastChoice];

      if (!this.props.skipGeolocation) {
        if (this.props.preferGeolocation) {
          methodsToTry.unshift(this.useGeoLocation);
        } else {
          methodsToTry.push(this.useGeoLocation);
        }
      }

      for (const method of methodsToTry) {
        try {
          if (await method()) { break; }
        } catch (error) {
          // do nothing
        }
      }
    }

    const {selection} = this.props;

    if (selection.address_id) {
      this.selectedAddress = selection.address_id;
    }

    //  In case we only have place_id in the selection state, we don't know if it's a saved address,
    //  so we load the custom address search field.
    if (!selection.address_id && selection.place_id && this.props.canLoadCustom && !this.isCleanLoad) {
      this.useSavedAddresses = false;
    }

    this.listeners = [
      reaction(
        () => this.selectedAddress,
        (address) => {
          if (address && address !== 'newAddress') {
            const addressValue = this.addressTypesList[address] && this.addressTypesList[address].value;

            if (addressValue) {
              this.props.selection.address_id = address;
              this.useSavedAddresses = true;

              this.props.onSelect({
                term: '',
                name: '',
                value: addressValue,
              });
            }
          }

          if (address === 'newAddress') {
            this.props.onClear();
          }
        },
        {
          name: 'Run select address function when user selects one custom address',
          compareStructural: true,
          fireImmediately: true,
        },
      ),
      reaction(
        () => this.typeaheadField,
        (typeaheadField) => {
          if (typeaheadField && this.props.canLoadCustom && this.props.defaultValue) {
            this.typeaheadField.setValue(this.props.defaultValue);
          }
        },
        {
          name: 'Update custom address field with value of selected address',
          fireImmediately: true,
        },
      ),
      reaction(
        () => this.addressesSelectList,
        (addressList) => {
          if (
            !this.selectedAddress &&
            addressList &&
            !this.props.disableSavedAddresses &&
            this.useSavedAddresses
          ) {
            this.selectedAddress = addressList[0].value;
          }
        },
        {
          name: 'Select first saved address by default',
          compareStructural: true,
          fireImmediately: true,
        },
      ),
      reaction(
        () => this.props.session && this.props.session.user,
        (user) => {
          if (user) {
            const {userAddresses} = this.props;
            userAddresses.maybeLoad();
          } else {
            this.selectedAddress = undefined;
          }
        },
        {
          name: 'Load user addresses when user signs in',
          compareStructural: true,
          fireImmediately: true,
        },
      ),
    ];
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.defaultValue !== nextProps.defaultValue &&
      nextProps.defaultValue != null &&
      this.typeaheadField
    ) {
      this.typeaheadField.setValue(nextProps.defaultValue);
    }
  }

  componentWillUnmount() {
    while (this.listeners && this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable selectedAddress = undefined;
  @observable addressTypesList = [];
  @observable useSavedAddresses = true;
  @observable typeaheadField = undefined;

  @autobind
  async loadLastChoice() {
    const lastTypeaheadSelection = await this.props.session.getCachedMeta('lastTypeaheadSelection');
    if (lastTypeaheadSelection) {
      const {resultSource, result_id, term} = lastTypeaheadSelection;

      if (term) {
        this.typeaheadField.setValue(term);
      }

      if (this.props.onSelect) {
        this.props.onSelect({
          term,
          name: term,
          value: `${resultSource}:${result_id}`,
        });
      }
      return true;
    }

    const lastSearchPostcode = await this.props.session.getCachedMeta('lastSearchPostcode');
    if (lastSearchPostcode) {
      this.typeaheadField.setValue(lastSearchPostcode);
      if (this.props.onSelect) {
        this.props.onSelect({
          term: lastSearchPostcode,
          name: lastSearchPostcode,
          value: `ukpostcode:${lastSearchPostcode}`,
        });
      }
      return true;
    }
    return false;
  }

  @autobind
  async useGeoLocation() {
    if (this.props.onSelect) {
      const position = await this.props.geolocation.maybeLoad();
      const result_id = `${position.coords.latitude},${position.coords.longitude}`;
      const place_id = `latlng:${result_id}`;
      this.props.onSelect({
        term: '',
        name: '',
        value: place_id,
      });
      return true;
    }
    return false;
  }

  /*
   *  onClick - when user clicks the custom address search field
   */
  @autobind onClick(event) {
    if (event) event.preventDefault();
    this.props.tracker.track('Clicked Location Address Field', {
      category: this.props.trackingCategory,
    });
  }

  /*
   *  changeAddress - when user changes the address
   */
  @autobind
  @action('changeAddress - Address field component')
  changeAddress(event) {
    const {tracker, selection} = this.props;
    const {address_id} = selection;

    this.selectedAddress = event.target.value;

    if (this.selectedAddress === 'newAddress') {
      this.useSavedAddresses = false;
      this.props.selection.address_id = undefined;
      setTimeout(() => { this.typeaheadField.setValue(''); }, 100);
    } else {
      this.props.selection.address_id = this.selectedAddress;
    }
  }

  /*
   *  onClickClear - user clears custom address field
   */
  @autobind
  @action('onClickClear - Address field component')
  onClickClear(event) {
    setTimeout(() => { this.typeaheadField.setValue(''); }, 100);

    this.props.onClear();
  }

  /*
   *  userAddressesList - List of available user saved addresses
   */
  @computed get userAddressesList() {
    const {userAddresses} = this.props;
    if (!userAddresses) return null;
    const {allAddresses} = userAddresses;

    return allAddresses.length > 0 ? allAddresses : null;
  }

  /*
   *  addressesSelectList - object with list of user saved addresses for the select element
   */
  @computed get addressesSelectList() {
    const {i18n} = this.context;
    const {userAddressesList} = this;
    if (!userAddressesList) return null;

    userAddressesList.forEach((address) => {
      this.addressTypesList[address._id] = {
        type: address.type,
        value: `latlng:${address.lat},${address.lng}`,
      };
    });

    const addressOptions = userAddressesList.map((address) => {
      const {type} = address;
      let label = '';

      switch (type) {
      case 'custom':
        label = i18n._x('Custom', 'Address field - saved addresses selector');
        break;
      case 'home':
        label = i18n._x('Home', 'Address field - saved addresses selector');
        break;
      case 'work':
        label = i18n._x('Work', 'Address field - saved addresses selector');
        break;
      default:
        label = i18n._x('Custom', 'Address field - saved addresses selector');
      }

      return {
        key: address._id,
        label,
        subtext: `${address.address1}, ${address.postcode}`,
        value: address._id,
        className: `address-field__option address-field__option--${address.type}`,
      };
    });

    addressOptions[addressOptions.length] = {
      label: i18n._x('New Address', 'Address search field component'),
      value: 'newAddress',
      className: `address-field__option address-field__option--new`,
    };

    return addressOptions;
  }

  /*
   *  getSuggestionsForString - get address sugestions as user types
   */
  getSuggestionsForString = term =>
    this.props.sdk.get(`/search/location/typeahead?term=${encodeURIComponent(term)}`)
      .then(({json}) =>
        json.results.map(({
          description, result_id, resultSource, matched_substrings,
        }) => {
          // split on last comma
          const parts = description.split(ADDRESS_PARTS_SEP);
          let subtext = '';
          if (parts.length > 2) {
            subtext = ADDRESS_PARTS_SEP +
              parts
                .splice(parts.length - 2, 2)
                .join(ADDRESS_PARTS_SEP);
          } else if (parts.length > 1) {
            subtext = ADDRESS_PARTS_SEP + parts.pop();
          }
          const name = parts.join(ADDRESS_PARTS_SEP);

          return {
            term: description,
            name,
            subtext,
            matched_substrings,
            value: `${resultSource}:${result_id}`,
            resultSource,
          };
        }));

  render() {
    const {userAddressesList, useSavedAddresses, onClickClear} = this;
    const {i18n} = this.context;
    /* eslint-disable no-unused-vars */
    const {
      addressConfirmed,
      align,
      canLoadCustom,
      className,
      clearStyles,
      darkBackground,
      defaultValue,
      disableSavedAddresses,
      geolocation,
      hideClearOnEmpty,
      includeClear,
      listClassName,
      location,
      onClear,
      params,
      placeholder,
      preferGeolocation,
      roundBorder,
      router,
      routes,
      sdk,
      selectedAddressId,
      selection,
      selectZIndex,
      session,
      skipGeolocation,
      skipInitialLoad,
      tracker,
      trackingCategory,
      userAddresses,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    if (!userAddressesList || !useSavedAddresses || disableSavedAddresses) {
      return (
        <TypeaheadField
          hideClearOnEmpty={hideClearOnEmpty}
          placeholder={placeholder || i18n.__('enter your address')}
          type="text"
          {...rest}
          ref={typeaheadField => Object.assign(this, {typeaheadField})}
          className={`form-control address-field ${className || ''}`}
          getSuggestionsForString={this.getSuggestionsForString}
          name="address-field-typeahead"
          selectFirstOptionOnEnterKey
          onClear={onClickClear}
          includeClear
          clearStyles={clearStyles}
          onClick={this.onClick}
          zIndex={selectZIndex}
        />
      );
    }

    return (
      <Select
        align={align}
        className={cn(
          'address-field__select',
          darkBackground && 'address-field__select--dark',
          roundBorder && 'address-field__select--roundBorder',
          this.addressTypesList[this.selectedAddress] && `address-field__select--${this.addressTypesList[this.selectedAddress].type}`,
        )}
        classNameDisabled=""
        classNameOpen=""
        listClassName={listClassName}
        name="address-field"
        onChange={this.changeAddress}
        options={this.addressesSelectList}
        value={this.selectedAddress}
        zIndex={selectZIndex}
      />
    );
  }
}

export default AddressField;



// WEBPACK FOOTER //
// ./spa/Components/AddressField.jsx